import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Review, ReviewCreation, ReviewInput } from '@skop-app/lib-domain/types'
import { Toast } from '@skop-app/lib-front'
import { useTranslations } from 'next-intl'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  createReviewMutation,
  updateReviewMutation,
} from '@/services/graphql/mutations'
import {
  applyOptimisticReviewChange,
  cancelReviewQueries,
  collectReviewMutationContext,
  restoreReviewMutationContext,
  revalidateReviewQueries,
} from '@/services/mutations/reviewCache'
import { trackUsageEvent } from '@/utils/posthog.utils'
import { getBugsnag } from '@/utils/bugsnag.client'
import { getQuotaErrorMessage } from '@/services/errors/quotaErrors'
import { isMaintenanceModeError } from '@/services/writeMaintenance'

const client = getAmplifyClient()

export { applyOptimisticReviewChange } from '@/services/mutations/reviewCache'

type UpsertReviewParams = {
  upsertType: 'create' | 'update'
  input: Partial<Review>
}

const fieldsToRemove = ['createdAt', 'updatedAt', 'byUser']

const createReview = async (
  variables: ReviewInput & { queryKey?: string[] },
) => {
  const { queryKey: _queryKey, ...rest } = variables

  const { data } = (await client.graphql({
    query: createReviewMutation,
    variables: { input: rest },
  })) as GraphQLResult<ReviewCreation>

  return data
}

export const upsertReviewRequest = async ({
  upsertType,
  input,
}: UpsertReviewParams) => {
  const review = { ...input }
  fieldsToRemove.forEach((field) => delete review[field])

  return client.graphql({
    query:
      upsertType === 'create' ? createReviewMutation : updateReviewMutation,
    variables: { input: review },
  })
}

export const useCreateReview = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createReview'],
    mutationFn: createReview,
    onMutate: async ({ queryKey: _queryKey, ...variables }) => {
      await cancelReviewQueries({
        queryClient,
        review: variables,
        reviewId: variables.id,
        includeDetail: true,
        includeDrawer: true,
        includeGrid: true,
        includeCount: true,
      })

      const context = collectReviewMutationContext({
        queryClient,
        review: variables,
        reviewId: variables.id,
        includeDetail: true,
        includeDrawer: true,
        includeGrid: true,
        includeCount: true,
      })

      applyOptimisticReviewChange({
        queryClient,
        mode: 'create',
        input: {
          ...variables,
          createdAt: new Date().toISOString(),
        },
      })

      return context
    },
    onError: (_error, _variables, context) => {
      restoreReviewMutationContext({ queryClient, context })
    },
    onSuccess: async (_data, variables) => {
      await revalidateReviewQueries({
        queryClient,
        review: variables,
        reviewId: variables.id,
        includeDetail: true,
        includeMessages: true,
        includeDrawer: true,
        includeGrid: true,
        includeCount: true,
        includeNotifications: true,
        refetchDetail: true,
      })
      trackUsageEvent('feature_used', {
        feature: 'review',
        action: 'create_review',
        surface: 'review_workflow',
        result: 'success',
      })
    },
  })
}

export const useUpsertReview = () => {
  const queryClient = useQueryClient()
  const t = useTranslations()

  return useMutation({
    mutationKey: ['upsertReview'],
    mutationFn: upsertReviewRequest,
    onMutate: async ({ upsertType, input }) => {
      await cancelReviewQueries({
        queryClient,
        review: input,
        reviewId: input.id,
        includeDetail: true,
        includeDrawer: true,
        includeGrid: true,
        includeCount: true,
      })

      const context = collectReviewMutationContext({
        queryClient,
        review: input,
        reviewId: input.id,
        includeDetail: true,
        includeDrawer: true,
        includeGrid: true,
        includeCount: true,
      })

      applyOptimisticReviewChange({
        queryClient,
        mode: upsertType,
        input: {
          ...input,
        },
      })

      return context
    },
    onError: (error, variables, context) => {
      restoreReviewMutationContext({ queryClient, context })
      // These business rejections already have a message in the global cache.
      if (isMaintenanceModeError(error) || getQuotaErrorMessage(error)) return

      // upsertReview has no entry in the mutation message catalog.
      Toast.error(t('Common.error'))
      try {
        getBugsnag()?.notify(
          new Error('Review save failed', {
            cause: error instanceof Error ? error : undefined,
          }),
          (event) => {
            event.addMetadata('reviewMutation', {
              operation: variables.upsertType,
              reviewId: variables.input.id,
            })
          },
        )
      } catch {
        // Reporting must not interfere with rollback or failure handling.
      }
    },
    onSuccess: async (_data, variables) => {
      await revalidateReviewQueries({
        queryClient,
        review: variables.input,
        reviewId: variables.input.id,
        includeDetail: true,
        includeMessages: true,
        includeDrawer: true,
        includeGrid: true,
        includeCount: true,
        includeNotifications: true,
        refetchDetail: true,
      })
      trackUsageEvent('feature_used', {
        feature: 'review',
        action:
          variables.upsertType === 'create' ? 'create_review' : 'update_review',
        surface: 'review_workflow',
        result: 'success',
      })
    },
  })
}
